import { ReportResponse, PersonnelReportData } from '../../../api/reports.api';

interface Props {
  data: ReportResponse<PersonnelReportData>;
}

const PersonnelReport = ({ data }: Props) => {
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('tr-TR');
  };

  return (
    <div className="p-6">
      {/* Report Header */}
      <div className="mb-6 pb-4 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900">Personel Bazlı Cihaz Raporu</h2>
        <div className="mt-2 text-sm text-gray-600">
          <span>Oluşturulma Tarihi: {formatDate(data.generatedAt)}</span>
        </div>
      </div>

      {/* Summary */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2">Özet</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Toplam Personel:</span>
            <span className="ml-2 font-semibold">{data.summary.totalPersonnel}</span>
          </div>
          <div>
            <span className="text-gray-600">Toplam Zimmetli Cihaz:</span>
            <span className="ml-2 font-semibold">{data.summary.totalDevices}</span>
          </div>
        </div>
      </div>

      {/* Personnel Data */}
      {data.data.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Seçilen kriterlere uygun veri bulunamadı
        </div>
      ) : (
        <div className="space-y-6">
          {data.data.map((personnel) => (
            <div key={personnel.personnelId} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Personnel Header */}
              <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
                <h4 className="font-semibold text-gray-900 text-lg">{personnel.personnelName}</h4>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2 text-gray-900">{personnel.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Telefon:</span>
                    <span className="ml-2 text-gray-900">{personnel.phone}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Departman:</span>
                    <span className="ml-2 text-gray-900">{personnel.department}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Zimmetli Cihaz:</span>
                    <span className="ml-2 font-semibold text-blue-600">{personnel.totalDevices}</span>
                  </div>
                </div>
              </div>

              {/* Categories within Personnel */}
              <div className="p-4 space-y-4">
                {personnel.categories && personnel.categories.length > 0 ? (
                  personnel.categories.map((category: any) => (
                    <div key={category.categoryId} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Category Header */}
                      <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
                        <h5 className="font-semibold text-blue-900">
                          {category.categoryName} <span className="text-sm font-normal text-blue-700">({category.totalDevices} cihaz)</span>
                        </h5>
                      </div>

                      {/* Devices Table */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seri No</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ürün</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marka</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kondisyon</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zimmet Tarihi</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {category.devices.map((device: any) => (
                              <tr key={device.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">{device.serialNumber}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{device.productName}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{device.brand}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{device.model}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                    device.status === 'Available' ? 'bg-green-100 text-green-800' :
                                    device.status === 'Assigned' ? 'bg-blue-100 text-blue-800' :
                                    device.status === 'InRepair' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {device.status === 'Available' ? 'Şubede' :
                                     device.status === 'Assigned' ? 'Zimmetli' :
                                     device.status === 'InRepair' ? 'Onarımda' :
                                     device.status === 'Scrapped' ? 'Hurda' :
                                     device.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">{device.condition}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {device.assignedDate ? formatDate(device.assignedDate) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Bu personelde zimmetli cihaz bulunamadı
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PersonnelReport;
